// src/views/Class_Master/CourseContentPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Table,
  Spinner,
  Alert,
} from 'reactstrap';

const API_BASE = 'http://localhost:6203/api';
const FILE_BASE = 'http://localhost:6203'; // syllabus files ka base URL

const CourseContentPage = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [courseContent, setCourseContent] = useState(null);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingCourse, setLoadingCourse] = useState(false);
  const [error, setError] = useState('');

  // ✅ optional semester filter (ALL by default)
  const [semesterFilter, setSemesterFilter] = useState('ALL');

  // ------------------------------------------------------------------
  // 1. Class dropdown load
  // ------------------------------------------------------------------
  useEffect(() => {
    const loadClasses = async () => {
      try {
        setLoadingClasses(true);
        setError('');

        const url = `${API_BASE}/ClassMaster/GetClassListForDropdown`;
        console.log('Calling Class dropdown API:', url);

        const res = await fetch(url, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        console.log('Dropdown API raw JSON:', data);

        const finalList = Array.isArray(data) ? data : [];
        setClasses(finalList);
      } catch (err) {
        console.error('Error in loadClasses:', err);
        setError('Class list load karte waqt error aaya.');
        setClasses([]);
      } finally {
        setLoadingClasses(false);
      }
    };

    loadClasses();
  }, []);

  // ------------------------------------------------------------------
  // 2. Class change → course content load
  // ------------------------------------------------------------------
  const handleClassChange = async (e) => {
    const val = e.target.value;
    setSelectedClassId(val);
    setCourseContent(null);
    setError('');
    setSemesterFilter('ALL'); // naya class choose par filter reset

    if (!val) return;

    try {
      setLoadingCourse(true);

      const url = `${API_BASE}/ClassMaster/GetCourseContentByClassId?class_id=${val}`;
      console.log('Calling course content API:', url);

      const res = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const raw = await res.json();
      console.log('Course content raw JSON:', raw);

      const isSuccess = raw?.status === true || raw?.Status === true;
      const data = raw?.data || raw?.Data || null;

      if (!isSuccess || !data) {
        setCourseContent(null);
        setError(raw?.message || raw?.Message || 'Course content nahi mila.');
        return;
      }

      setCourseContent(data);
    } catch (err) {
      console.error('Error loading course content:', err);
      setError('Course content load karte waqt error aaya.');
    } finally {
      setLoadingCourse(false);
    }
  };

  const pick = (obj, keys, fallback = '-') => {
    for (const k of keys) {
      if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== '') {
        return obj[k];
      }
    }
    return fallback;
  };

  // ------------------------------------------------------------------
  // 3. Semesters array (server se) + fallback grouping
  // ------------------------------------------------------------------
  const semesterBlocks = useMemo(() => {
    if (!courseContent) return [];

    // Prefer server side Semesters
    let sems = courseContent.semesters || courseContent.Semesters || null;

    if (Array.isArray(sems) && sems.length > 0) {
      sems = [...sems].sort((a, b) => {
        const sa = (a.semester || a.Semester || '').toString();
        const sb = (b.semester || b.Semester || '').toString();
        const na = parseInt(sa, 10);
        const nb = parseInt(sb, 10);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return sa.localeCompare(sb);
      });
      return sems;
    }

    // Fallback: Subjects se group
    const subjects = courseContent.subjects || courseContent.Subjects || [];
    if (!Array.isArray(subjects) || subjects.length === 0) return [];

    const map = subjects.reduce((acc, subj) => {
      const semRaw = subj.semester ?? subj.Semester ?? 'No Semester';
      const sem = semRaw.toString().trim() || 'No Semester';
      if (!acc[sem]) acc[sem] = [];
      acc[sem].push(subj);
      return acc;
    }, {});

    const keys = Object.keys(map).sort((a, b) => {
      const na = parseInt(a, 10);
      const nb = parseInt(b, 10);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.localeCompare(b);
    });

    return keys.map((k) => ({
      Semester: k,
      Subjects: map[k],
    }));
  }, [courseContent]);

  const hasSemesters = semesterBlocks.length > 0;

  // Distinct semester keys for filter dropdown
  const semesterKeys = useMemo(() => {
    return semesterBlocks.map((b) =>
      (b.Semester || b.semester || 'No Semester').toString()
    );
  }, [semesterBlocks]);

  // Filter apply
  const filteredSemesterBlocks = useMemo(() => {
    if (!hasSemesters) return [];
    if (!semesterFilter || semesterFilter === 'ALL') return semesterBlocks;

    return semesterBlocks.filter((b) => {
      const k = (b.Semester || b.semester || 'No Semester').toString();
      return k === semesterFilter;
    });
  }, [semesterBlocks, semesterFilter, hasSemesters]);

  // ------------------------------------------------------------------
  // 4. UI
  // ------------------------------------------------------------------
  return (
    <div className='container-fluid mt-3'>
      <Row>
        <Col md='12'>
          <Card className='card-box height-100-p pd-20 mb-20'>
            <CardHeader>
              <CardTitle tag='h5'>
                Course Content (Class &amp; Semester wise)
              </CardTitle>
            </CardHeader>
            <CardBody>
              {/* Class + Semester filter row */}
              <Row>
                <Col md='4'>
                  <FormGroup>
                    <Label htmlFor='classSelect'>Class</Label>
                    {loadingClasses ? (
                      <div className='d-flex align-items-center'>
                        <Spinner size='sm' className='mr-2' /> Loading
                        classes...
                      </div>
                    ) : (
                      <Input
                        type='select'
                        id='classSelect'
                        value={selectedClassId}
                        onChange={handleClassChange}
                      >
                        <option value=''>-- Select Class --</option>
                        {classes.map((cls, index) => {
                          const id = cls.id ?? cls.Id ?? cls.ID ?? index;
                          return (
                            <option key={id} value={id}>
                              {cls.class_display_name ||
                                cls.class_display_Name ||
                                cls.class_Name ||
                                `Class #${id}`}
                            </option>
                          );
                        })}
                      </Input>
                    )}
                  </FormGroup>

                  {!loadingClasses && !error && classes.length === 0 && (
                    <Alert color='info' className='mt-2'>
                      Koi class record nahi mila. Pehle Class Master me data add
                      karo.
                    </Alert>
                  )}
                </Col>

                {/* ✅ Optional Semester filter */}
                <Col md='4'>
                  <FormGroup>
                    <Label htmlFor='semFilter'>Semester (optional)</Label>
                    <Input
                      type='select'
                      id='semFilter'
                      value={semesterFilter}
                      onChange={(e) => setSemesterFilter(e.target.value)}
                      disabled={!hasSemesters}
                    >
                      <option value='ALL'>-- All Semesters --</option>
                      {semesterKeys.map((k) => (
                        <option key={k} value={k}>
                          {k === 'No Semester' ? 'No Semester' : `Semester ${k}`}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              {/* Error message */}
              {error && (
                <Row className='mt-2'>
                  <Col md='12'>
                    <Alert color='danger'>{error}</Alert>
                  </Col>
                </Row>
              )}

              {/* Course basic info */}
              {courseContent && (
                <Row className='mt-3'>
                  <Col md='3'>
                    <strong>Class:</strong>{' '}
                    {pick(courseContent, [
                      'classDisplayName',
                      'ClassDisplayName',
                      'class_display_name',
                      'className',
                      'ClassName',
                      'class_Name',
                    ])}
                  </Col>
                  <Col md='3'>
                    <strong>Code:</strong>{' '}
                    {pick(courseContent, [
                      'classCode',
                      'ClassCode',
                      'class_code',
                    ])}
                  </Col>
                  <Col md='3'>
                    <strong>Duration:</strong>{' '}
                    {pick(courseContent, ['duration', 'Duration'])}
                  </Col>
                  <Col md='3'>
                    <strong>Total Semester:</strong>{' '}
                    {pick(courseContent, [
                      'totalSemester',
                      'TotalSemester',
                      'total_semester',
                    ])}
                  </Col>
                </Row>
              )}

              {/* Loading course */}
              {loadingCourse && (
                <Row className='mt-3'>
                  <Col md='12' className='d-flex align-items-center'>
                    <Spinner size='sm' className='mr-2' /> Loading course
                    content...
                  </Col>
                </Row>
              )}

              {/* Annexe-style: each semester separate table */}
              {courseContent &&
                !loadingCourse &&
                filteredSemesterBlocks.length > 0 && (
                  <Row className='mt-4'>
                    <Col md='12'>
                      {filteredSemesterBlocks.map((semBlock, idx) => {
                        const semKey =
                          semBlock.Semester ||
                          semBlock.semester ||
                          `Sem-${idx + 1}`;
                        const subjects =
                          semBlock.Subjects || semBlock.subjects || [];

                        return (
                          <div key={semKey} className='mb-4'>
                            <h6 className='mb-2'>
                              {semKey === 'No Semester'
                                ? 'Subjects (No Semester)'
                                : `Semester ${semKey}`}
                            </h6>

                            <Table striped hover hover size='sm'>
                              <thead>
                                <tr>
                                  <th style={{ width: '60px' }}>#</th>
                                  <th>Subject Code</th>
                                  <th>Subject Name</th>
                                  <th>Syllabus File</th>
                                  <th style={{ width: '120px' }}>Marks</th>
                                </tr>
                              </thead>
                              <tbody>
                                {subjects.map((subj, sIdx) => {
                                  // ✅ Syllabus link banana
                                  const rawPath = pick(subj, [
                                    'SyllabusFile',
                                    'syllabusFile',
                                    'syllabus_file',
                                  ]);

                                  let syllabusCell = '-';

                                  if (rawPath && rawPath !== '-') {
                                    const cleanPath = rawPath.startsWith('/')
                                      ? rawPath
                                      : `/${rawPath}`;
                                    const url = `${FILE_BASE}${cleanPath}`;

                                    syllabusCell = (
                                      <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          textDecoration: 'underline',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        View Syllabus PDF
                                      </a>
                                    );
                                  }

                                  return (
                                    <tr
                                      key={
                                        subj.SubjectId ||
                                        subj.SubjectID ||
                                        subj.id ||
                                        sIdx
                                      }
                                    >
                                      <td>{sIdx + 1}</td>
                                      <td>
                                        {pick(subj, [
                                          'SubjectCode',
                                          'subjectCode',
                                          'subject_code',
                                        ])}
                                      </td>
                                      <td>
                                        {pick(subj, [
                                          'SubjectName',
                                          'subjectName',
                                          'subject_name',
                                        ])}
                                      </td>
                                      <td>{syllabusCell}</td>
                                      <td>
                                        {pick(subj, [
                                          'SubjectMarks',
                                          'subjectMarks',
                                          'subject_marks',
                                        ])}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        );
                      })}
                    </Col>
                  </Row>
                )}

              {/* No subjects */}
              {courseContent &&
                !loadingCourse &&
                filteredSemesterBlocks.length === 0 && (
                  <Row className='mt-3'>
                    <Col md='12'>
                      <Alert color='info'>
                        Is filter ke liye koi subjects define nahi kiye gaye.
                      </Alert>
                    </Col>
                  </Row>
                )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CourseContentPage;
